import { useTranslation } from '@/utils/hook'
import Link from 'next/link'
import { Fragment } from 'react'
import { Popover, Transition } from '@headlessui/react'
import { IconX } from '@tabler/icons-react'
import Image from 'next/image'

const navItems = [
  {
    href: '/product',
    label: 'common.product'
  },
  {
    href: '/templates',
    label: 'common.templates'
  },
  {
    href: '/integrations',
    label: 'common.integrations'
  },
  {
    href: '/pricing',
    label: 'common.pricing'
  },
  {
    href: '/blog',
    label: 'common.blog'
  }
]

export function Header() {
  const t = useTranslation()

  return (
    <Popover className="fixed inset-x-0 top-0 z-[9999] isolate px-4 py-4 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-3xl justify-center">
        <div className="relative z-10 flex items-center justify-between rounded-full border border-black/10 bg-white p-2 shadow-[0_18px_60px_rgba(15,23,42,0.18)] md:px-3">
          <div className="flex items-center md:gap-2">
            <Link href="/">
              <span className="sr-only">HeyForm</span>
              <div className="flex items-center rounded-full p-2 text-[15px] font-semibold tracking-[-0.02em] text-slate-950">
                <span className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full ring-1 ring-black/5">
                  <Image
                    src="/static/favicon.png"
                    alt="HeyForm"
                    width={32}
                    height={32}
                    className="h-8 w-8 rounded-full object-cover"
                  />
                </span>
                <span className="ml-2 text-xl md:hidden">HeyForm</span>
              </div>
            </Link>

            <Popover.Group as="nav" className="hidden items-center gap-0.5 md:flex mr-2">
              {navItems.map(item => (
                <Link
                  key={item.href}
                  href={item.href}
                  className="rounded-full px-3 py-2 text-[15px] font-medium text-slate-700 transition-colors hover:bg-slate-100 hover:text-slate-950"
                >
                  {t(item.label)}
                </Link>
              ))}
            </Popover.Group>
          </div>

          <div className="ml-2 hidden items-center gap-2 md:flex md:ml-auto">
            <Link
              href="https://my.heyform.net/sign-up"
              className="inline-flex items-center justify-center rounded-full bg-slate-950 px-5 py-3 text-[15px] font-medium text-white shadow-[0_8px_24px_rgba(15,23,42,0.18)] transition-transform duration-200 hover:-translate-y-0.5 hover:bg-black"
            >
              Get started
            </Link>
          </div>

          <div className="md:hidden">
            <Link
              href="https://my.heyform.net/sign-up"
              className="inline-flex items-center justify-center rounded-full bg-slate-950 px-5 py-3 text-[15px] font-medium text-white shadow-[0_8px_24px_rgba(15,23,42,0.18)] transition-colors hover:bg-black"
            >
              Get started
            </Link>
          </div>
        </div>
      </div>

      <Transition
        as={Fragment}
        enter="duration-200 ease-out"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="duration-100 ease-in"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <Popover.Panel
          focus
          className="absolute inset-x-0 top-0 z-[202] origin-top-right px-4 pt-4 transition md:hidden"
        >
          <div className="mx-auto max-w-3xl rounded-[2rem] border border-black/5 bg-white p-4 shadow-[0_20px_48px_rgba(15,23,42,0.16)]">
            <div className="flex items-center justify-between px-1 pb-5">
              <Link href="/">
                <span className="sr-only">HeyForm</span>
                <div className="flex items-center rounded-full bg-[#f6f1eb] px-2 py-2 text-[15px] font-semibold tracking-[-0.02em] text-slate-950">
                  <span className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full ring-1 ring-black/5">
                    <Image
                      src="/static/favicon.png"
                      alt="HeyForm"
                      width={32}
                      height={32}
                      className="h-8 w-8 rounded-full object-cover"
                    />
                  </span>
                </div>
              </Link>

              <Popover.Button className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-600 transition-colors hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300">
                <span className="sr-only">Close menu</span>
                <IconX className="h-6 w-6" aria-hidden="true" />
              </Popover.Button>
            </div>

            <div className="space-y-6 rounded-[1.5rem] bg-[#faf7f3] p-4">
              <div className="grid gap-2">
                {navItems.map(item => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className="rounded-2xl bg-white px-4 py-3 text-base font-medium text-slate-900 shadow-[0_2px_10px_rgba(15,23,42,0.04)] transition-colors hover:bg-slate-50"
                  >
                    {t(item.label)}
                  </Link>
                ))}
              </div>

              <div className="space-y-3">
                <Link
                  href="https://my.heyform.net/sign-up"
                  className="inline-flex w-full items-center justify-center rounded-full bg-slate-950 px-5 py-3 text-base font-medium text-white shadow-[0_8px_24px_rgba(15,23,42,0.18)] transition-colors hover:bg-black"
                >
                  Get started
                </Link>
                <Link
                  href="https://my.heyform.net"
                  className="inline-flex w-full items-center justify-center rounded-full border border-slate-200 bg-white px-5 py-3 text-base font-medium text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-950"
                >
                  {t('common.login')}
                </Link>
              </div>
            </div>
          </div>
        </Popover.Panel>
      </Transition>
    </Popover>
  )
}
